import { CircleAlert, CircleCheck, Info } from "lucide-react";

import { cx } from "./cx";

/**
 * White box, radius 12, padding 16, the one shadow (none in dark mode).
 *
 * `flush`: no padding and no gap, for rows that reach the edges (Settings).
 * Not by adding p-0 in `className`: two padding classes on one element are
 * settled by the stylesheet's order, not by which comes last.
 */
export function Card({
  children,
  className,
  flush,
}: {
  children: React.ReactNode;
  className?: string;
  flush?: boolean;
}) {
  return (
    <section
      className={cx(
        "flex flex-col rounded-card bg-surface shadow-card dark:border dark:border-border",
        !flush && "gap-3 p-4",
        className,
      )}
    >
      {children}
    </section>
  );
}

/** A card's title. */
export function CardTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-heading font-bold text-text">{children}</h2>;
}

/** One fact on a card: what it is on the left (muted), the value on the right. */
export function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-label text-muted">{label}</span>
      <span className="text-right text-base font-semibold text-text tabular-nums">{value}</span>
    </div>
  );
}

export type BadgeTone = "neutral" | "danger" | "warn" | "primary";

const BADGE: Record<BadgeTone, string> = {
  neutral: "bg-disabled-surface text-body",
  danger: "bg-danger-surface text-danger",
  warn: "bg-warn-surface text-warn",
  primary: "bg-primary-soft text-primary",
};

/** A small label: Edited (warn), Cancelled (danger), Waiting, Expired, Owner/Staff. */
export function Badge({ tone = "neutral", children }: { tone?: BadgeTone; children: React.ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex h-6 items-center rounded-full px-2.5 text-small font-semibold whitespace-nowrap",
        BADGE[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * A message about the whole page (the phone's Banner): red for something that
 * went wrong, amber for something worth reading, green for something done.
 * Tinted, with a coloured edge
 * and an icon, so it is not read by colour alone.
 */
export function Banner({
  tone,
  children,
  action,
}: {
  tone: "error" | "warn" | "success";
  children: React.ReactNode;
  /** A link or button at the end: Try again, See plans. */
  action?: React.ReactNode;
}) {
  const error = tone === "error";
  const Icon = error ? CircleAlert : tone === "success" ? CircleCheck : Info;
  return (
    <div
      role={error ? "alert" : "status"}
      className={cx(
        "flex items-start gap-2 rounded-control border-l-[3px] p-3",
        error
          ? "border-danger bg-danger-surface text-danger"
          : tone === "success"
            ? "border-primary bg-primary-soft text-primary"
            : "border-warn bg-warn-surface text-warn",
      )}
    >
      <Icon size={20} aria-hidden="true" className="mt-px shrink-0" />
      <div className="flex-1 text-label">{children}</div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/** A list with nothing in it: the phone's words, centred and muted, and maybe a button. */
export function EmptyState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-4 px-4 py-10 text-center">
      <p className="max-w-md text-base text-muted">{message}</p>
      {action}
    </div>
  );
}
